import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {TrueGrit} from "../TrueGrit";
import {TrueGritStatics} from "../TrueGritStatics";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {SimpleReelModelEventData, SimpleReelModelState} from "../../core/reel/ReelInterfaces";
import {gsap} from "gsap";
import {BLEND_MODES} from "pixi.js";
import {TrueGritGameMode} from "../server/TrueGritGameMode";
import {TrueGritSound} from "../sound/TrueGritSound";
import {TrueGritModel} from "../TrueGritModel";
import {CustomBounce} from "gsap/dist/CustomBounce";

/**
 * Created by Jerker Nord on 2021-12-01.
 */

export class TrueGritAnticipationView extends PIXI.Container implements StageLayer {
    private _allGritGirlAnticipations: TimelineSprite[] = [];
    private _allGritGirlFadeInTLs: gsap.core.Timeline[] = [];
    private _allGritGirlFadeOutTLs: gsap.core.Timeline[] = [];

    private _allScatterAnticipations: TimelineSprite[] = [];
    private _allScatterFadeInTLs: gsap.core.Timeline[] = [];
    private _allScatterFadeOutTLs: gsap.core.Timeline[] = [];

    private _anticipationSoundPlayed: boolean = false;
    private _anticipationAddOnSoundPlayed: boolean = false;
    private _anticipationFailSoundPlayed: boolean = false;
    private _anticipationFailAddOnSoundPlayed: boolean = false;

    // #431
    private _lastActiveReelStopped: boolean = false;

    // #435
    private _nearWinBounce: gsap.core.Tween;
    private _stageInitY: number = 0;

    // #472 -> don't make it true for reel 1 on fill up, otherwise it may lead to issue again.
    private _isAnticipating: boolean = false;
    get isAnticipating(): boolean {
        return this._isAnticipating;
    }

    constructor() {
        super();
        this.name = "TrueGritAnticipationView";

        CustomBounce.create("shakeBounce", {strength: 0.8, squash: 0, endAtStart: true, squashID: "shake"});

        this.init();
    }

    public resetSoundFlags(): void {
        this._anticipationSoundPlayed = false;
        this._anticipationAddOnSoundPlayed = false;
        this._anticipationFailSoundPlayed = false;
        this._anticipationFailAddOnSoundPlayed = false;
        this._lastActiveReelStopped = false;
        this._isAnticipating = false;
    }

    // #462
    public tryAnticipation(reel: number): void {
        if (reel > 3) {
            return;
        }
        // Grit girl
        if (this._allGritGirlAnticipations[reel].alpha > 0) {
            this._allGritGirlFadeOutTLs[reel].restart();

            this.shakeStage();
        }
        const mode: string = TrueGrit.model.data.mode;
        if (mode.indexOf("AVALANCHE") > -1) {
            for (let i: number = (reel + 1); i < 4; i++) {
                if (TrueGrit.model.gritGirlNearWinReels[i] && this._allGritGirlAnticipations[i].alpha === 0) {
                    this._allGritGirlFadeInTLs[i].restart();
                    this.playAnticipationAddOnSound();
                    this._isAnticipating = true;

                    break;
                }
                // #445
                else if (TrueGrit.model.gritGirlNearWinReels[i] && this._allGritGirlAnticipations[i].alpha !== 0) {
                    break;
                }
            }
        } else {
            if (TrueGrit.model.gritGirlNearWinReels[reel + 1]) {
                this._allGritGirlFadeInTLs[reel + 1].restart();
                this.playAnticipationAddOnSound();
                this._isAnticipating = true;
            }
        }

        // Scatter
        if (this._allScatterAnticipations[reel].alpha > 0) {
            this._allScatterFadeOutTLs[reel].restart();

            this.shakeStage();
        }
        if (mode === TrueGritGameMode.NORMAL_AVALANCHE) {
            for (let i: number = (reel + 1); i < 4; i++) {
                if (TrueGrit.model.scatterNearWinReels[i] && this._allScatterAnticipations[i].alpha === 0) {
                    this._allScatterFadeInTLs[i].restart();
                    this.playAnticipationSound();
                    this._isAnticipating = true;

                    break;
                }
                // #445
                else if (TrueGrit.model.scatterNearWinReels[i] && this._allScatterAnticipations[i].alpha !== 0) {
                    break;
                }
            }
        } else {
            if (TrueGrit.model.scatterNearWinReels[reel + 1]) {
                this._allScatterFadeInTLs[reel + 1].restart();
                this.playAnticipationSound();
                this._isAnticipating = true;
            }
        }

        this.onReelStop(reel);
    }

    public startGritGirlReel1Anticipation(): void {
        this._allGritGirlFadeInTLs[0].restart();
        this.playAnticipationAddOnSound();
    }

    public startScatterReel1Anticipation(): void {
        this._allScatterFadeInTLs[0].restart();
        this.playAnticipationSound();
    }

    public stopAnticipationSounds(): void {
        // stop sounds
        const gritGirlsCount: number = TrueGrit.trueGritGritGirlSymbols.getAllUsedGritGirls().length;

        if (this._anticipationSoundPlayed) {
            TrueGrit.soundPlayer.stopSound(TrueGritSound.ANTICIPATION);
        }
        if (this._anticipationAddOnSoundPlayed) {
            TrueGrit.soundPlayer.stopSound(TrueGritSound.ANTICIPATION_ADD_ON);
        }
        if ((TrueGrit.model.data.nextMode.indexOf("FREESPIN") === -1) || gritGirlsCount === 2) {
            this.playAnticipationFailSound();
        }
        if (gritGirlsCount === 2) {
            this.playAnticipationFailAddOnSound();
        }
    }

    public onResize(): void {
        if (this._nearWinBounce && this._nearWinBounce.isActive()) {
            this._nearWinBounce.progress(1);

            this.parent.y = this._stageInitY;
        }

        this.position = TrueGritStatics.MAIN_REELS_VIEW_POSITION;
        if (!NolimitApplication.isLandscape) {
            this.position.y = this.position.y - (TrueGritStatics.PORTRAIT_Y_OFFSET + NolimitApplication.screenBounds.top) / 2;
        }
    }

    private init(): void {
        // Grit girl
        this.createGritGirlAnticipation();

        // Scatter
        this.createScatterAnticipation();

        for (let i: number = 0; i < 4; i++) {
            TrueGrit.allReelsView.mainReelModels[i].events?.on(SimpleReelModelState.STOPPED, (data: SimpleReelModelEventData) => this.onReelStop(data.reel));
        }

        this.onResize();
    }

    private createGritGirlAnticipation(): void {
        const totalItem: number = 4,
            baseX: number = 74,
            xIncrementer: number = 120,
            yPos: number = 130;

        for (let i: number = 0; i < totalItem; i++) {
            let item: TimelineSprite,
                fadeInTL: gsap.core.Timeline,
                fadeOutTL: gsap.core.Timeline;

            item = new TimelineSprite(TrueGrit.assetLoader.getTextures("winAnimations/gritGirlExcitement/excitement_"));
            item.anchor.set(0.5);
            item.position.set((baseX + (xIncrementer * i)), yPos);
            item.alpha = 0;
            item.tint = 0x101010;
            this.addChild(item);
            this._allGritGirlAnticipations.push(item);

            fadeInTL = new gsap.core.Timeline({paused: true});
            fadeInTL.add(() => {
                // #431
                if (!this._lastActiveReelStopped) {
                    item.alpha = 0;
                    item.show();
                    item.playLoop();
                }
            });
            fadeInTL.add(gsap.to(item, {duration: 0.2, alpha: 1}));
            this._allGritGirlFadeInTLs.push(fadeInTL);

            fadeOutTL = new gsap.core.Timeline({paused: true});
            fadeOutTL.add(() => {item.alpha = 1});
            fadeOutTL.add(gsap.to(item, {duration: 0.2, alpha: 0, onComplete: () => item.stopLoop()}));
            this._allGritGirlFadeOutTLs.push(fadeOutTL);
        }
    }

    private createScatterAnticipation(): void {
        const totalItem: number = 4,
            baseX: number = 74,
            xIncrementer: number = 120,
            yPos: number = 130;

        for (let i: number = 0; i < totalItem; i++) {
            let item: TimelineSprite,
                fadeInTL: gsap.core.Timeline,
                fadeOutTL: gsap.core.Timeline;

            item = new TimelineSprite(TrueGrit.assetLoader.getTextures("winAnimations/gritGirlExcitement/excitement_"));
            item.anchor.set(0.5);
            item.position.set((baseX + (xIncrementer * i)), yPos);
            item.alpha = 0;
            item.blendMode = BLEND_MODES.SCREEN;
            this.addChild(item);
            this._allScatterAnticipations.push(item);

            fadeInTL = new gsap.core.Timeline({paused: true});
            fadeInTL.add(() => {
                // #431
                if (!this._lastActiveReelStopped) {
                    item.alpha = 0;
                    item.show();
                    item.playLoop();
                }
            });
            fadeInTL.add(gsap.to(item, {duration: 0.2, alpha: 1}));
            this._allScatterFadeInTLs.push(fadeInTL);

            fadeOutTL = new gsap.core.Timeline({paused: true});
            fadeOutTL.add(() => {item.alpha = 1});
            fadeOutTL.add(gsap.to(item, {duration: 0.2, alpha: 0, onComplete: () => item.stopLoop()}));
            this._allScatterFadeOutTLs.push(fadeOutTL);
        }
    }

    private onReelStop(reel: number): void {
        if (reel === TrueGritModel.lastActiveReelInFirst4) {
            this._allGritGirlAnticipations.forEach((val: TimelineSprite) => {
                val.stopLoop();
                val.hide();
            });
            this._allScatterAnticipations.forEach((val: TimelineSprite) => {
                val.stopLoop();
                val.hide();
            });
            this._lastActiveReelStopped = true;
            this._isAnticipating = false;
        }
    }

    private playAnticipationSound(): void {
        if (!this._anticipationSoundPlayed) {
            TrueGrit.soundPlayer.playSound(TrueGritSound.ANTICIPATION);
            this._anticipationSoundPlayed = true;
        }
    }

    private playAnticipationAddOnSound(): void {
        if (!this._anticipationAddOnSoundPlayed) {
            TrueGrit.soundPlayer.playSound(TrueGritSound.ANTICIPATION_ADD_ON);
            this._anticipationAddOnSoundPlayed = true;
        }
        this.playAnticipationSound();
    }

    private playAnticipationFailSound(): void {
        if (this._anticipationSoundPlayed && !this._anticipationFailSoundPlayed) {
            TrueGrit.soundPlayer.playSound(TrueGritSound.ANTICIPATION_FAIL);
            this._anticipationFailSoundPlayed = true;
        }
    }

    private playAnticipationFailAddOnSound(): void {
        if (this._anticipationAddOnSoundPlayed && !this._anticipationFailAddOnSoundPlayed) {
            TrueGrit.soundPlayer.playSound(TrueGritSound.ANTICIPATION_FAIL_ADD_ON);
            this._anticipationFailAddOnSoundPlayed = true;
        }
    }

    // #435
    private shakeStage(): void {
        this._stageInitY = this.parent.y;

        if (this._nearWinBounce && this._nearWinBounce.isActive()) {
            this._nearWinBounce.progress(1);
        }

        this._nearWinBounce = gsap.to(this.parent, 0.2, {y: "+=" + 20, ease: "shakeBounce"});
    }
}